import { useEffect, useRef, useState } from "react";
import type { OsintReconConfig } from "../scenario/types";
import { audio } from "../lib/audio";
import { useSpeaking } from "../lib/useSpeaking";
import { tx, txr } from "../lib/tx";

/**
 * The attacker's OSINT recon console (attacker POV — no scoring). The learner
 * watches "kr0nos" turn a company name into a person:
 *   1. search public records / breach dumps -> a roster of employees + emails
 *   2. save a target list
 *   3. run a deep social scan on the chosen victim (Google reviews via Gaia id,
 *      Instagram by name/email) -> a profile of habits is assembled.
 * Full-screen cinematic over the dark hacker laptop, modeled on the deepfake
 * studio. A few guided clicks drive it; ends on Continue.
 */
type Phase = "search" | "results" | "saved" | "scan" | "profile";

const SOURCE_META: Record<string, { ico: string; label: string }> = {
  google_review: { ico: "\u2B50", label: "Google review (Gaia id)" },
  instagram: { ico: "\u{1F4F7}", label: "Instagram" },
};

export function OsintRecon({
  config,
  onContinue,
  onStageChange,
}: {
  config: OsintReconConfig;
  onContinue: () => void;
  onStageChange?: (phase: string) => void;
}) {
  const [phase, setPhase] = useState<Phase>("search");
  useEffect(() => {
    onStageChange?.(phase);
  }, [phase, onStageChange]);
  const [searching, setSearching] = useState(false);
  const [scanIdx, setScanIdx] = useState(-1);
  const mounted = useRef(true);
  const speaking = useSpeaking();
  const findings = config.target.findings ?? [];

  useEffect(() => {
    mounted.current = true;
    // Prefetch every line this console can speak: playback starts without a
    // gap, and the voice prerender (capture mode) records the scan line too,
    // which is only spoken after three clicks.
    const vopts = { voice: config.voiceId || "onyx", lang: config.language || "en-US" };
    if (config.line) void audio.prefetchSpeech(config.line, vopts);
    if (config.scanLine) void audio.prefetchSpeech(config.scanLine, vopts);
    if (config.line) {
      window.setTimeout(
        () => mounted.current && audio.speak(config.line!, { voice: config.voiceId || "onyx", lang: config.language || "en-US" }),
        160
      );
    }
    return () => {
      mounted.current = false;
      audio.cancelSpeech();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reveal social findings one at a time once scanning starts; the hacker gloats.
  useEffect(() => {
    if (phase !== "scan") return;
    const timers: number[] = [];
    if (config.scanLine) {
      timers.push(
        window.setTimeout(
          () => mounted.current && audio.speak(config.scanLine!, { voice: config.voiceId || "onyx", lang: config.language || "en-US" }),
          500
        )
      );
    }
    findings.forEach((_, i) => {
      timers.push(window.setTimeout(() => mounted.current && setScanIdx(i), 600 + i * 900));
    });
    timers.push(
      window.setTimeout(() => mounted.current && setPhase("profile"), 700 + findings.length * 900 + 400)
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const runSearch = () => {
    if (searching) return;
    setSearching(true);
    window.setTimeout(() => {
      if (!mounted.current) return;
      setSearching(false);
      setPhase("results");
    }, 1100);
  };

  // What the operator must do next — keeps the guided clicks obvious.
  const hint =
    phase === "search"
      ? searching
        ? "Enumerating staff\u2026"
        : "Step 1 \u2014 click \u201CSearch public records\u201D to enumerate the company\u2019s staff."
      : phase === "results"
        ? "Step 2 \u2014 click \u201CSave target list\u201D below."
        : phase === "saved"
          ? `Step 3 \u2014 click \u201CDeep-scan social profiles\u201D for ${config.target.name}.`
          : phase === "scan"
            ? "Scanning public footprint \u2014 harvesting habits\u2026"
            : "Profile assembled \u2014 build the spear-phish.";
  // Highlight the instruction whenever the learner has a button to click.
  const actionable =
    (phase === "search" && !searching) || phase === "results" || phase === "saved";

  return (
    <div className="osint">
      <div className="osint-app">
        <div className="osint-titlebar">
          <span className="osint-dot r" />
          <span className="osint-dot y" />
          <span className="osint-dot g" />
          <span className="osint-app-name">{config.appName ?? tx("ReconHound — OSINT toolkit")}</span>
          <span className="osint-badge">{tx("operator console")}</span>
        </div>

        <div className="osint-search">
          <span className="osint-search-ico" aria-hidden>{"\u{1F50D}"}</span>
          <span className="osint-search-q">{config.company}</span>
          {phase === "search" && !searching && (
            <span className="osint-clickhere osint-clickhere--left" aria-hidden onClick={runSearch}>
              {tx("Click here")} <span className="osint-clickhere-arrow">{"\u2192"}</span>
            </span>
          )}
          {phase === "search" && (
            <button
              type="button"
              className={`osint-run ${searching ? "" : "go"}`}
              onClick={runSearch}
              disabled={searching}
            >
              {searching ? tx("Searching…") : tx("🔍 Search public records")}
            </button>
          )}
          {phase !== "search" && <span className="osint-search-done">{"\u2713"} {tx("enumerated")}</span>}
        </div>

        <div className={`osint-instruction ${actionable ? "go" : ""}`} role="status">
          <span className="osint-instruction-ico" aria-hidden>{phase === "saved" ? "\u{1F578}" : "\u203A"}</span>
          <span className="osint-instruction-text">{hint}</span>
        </div>

        <div className="osint-body">
          {/* Left: employee roster + target list */}
          <div className="osint-left">
            <div className="osint-card-h">
              {txr("Employees found {v}", { v: phase !== "search" ? `· ${config.employees.length}` : "" })}
            </div>
            {phase === "search" ? (
              <div className="osint-empty">
                {searching ? tx("Querying breach dumps + LinkedIn + WHOIS…") : tx("Run the search to enumerate staff.")}
              </div>
            ) : (
              <>
                <ul className="osint-people">
                  {config.employees.map((e, i) => (
                    <li key={i} className={`osint-person ${e.target ? "target" : ""}`}>
                      <span className="osint-person-pic">
                        {e.photoUrl ? <img src={e.photoUrl} alt="" /> : e.name.slice(0, 1)}
                      </span>
                      <span className="osint-person-text">
                        <span className="osint-person-name">{e.name}</span>
                        <span className="osint-person-role">{e.role}</span>
                        <span className="osint-person-mail">{e.email}</span>
                      </span>
                      {e.target && <span className="osint-target-chip">{tx("target")}</span>}
                    </li>
                  ))}
                </ul>
                {phase === "results" && (
                  <div className="osint-actrow">
                    <button type="button" className="osint-run wide go" onClick={() => setPhase("saved")}>
                      {"\u2B07"} {txr("Save target list ({length}", { length: config.employees.length })})
                    </button>
                    <span className="osint-clickhere osint-clickhere--right" aria-hidden onClick={() => setPhase("saved")}>
                      <span className="osint-clickhere-arrow">{"\u2190"}</span> {tx("Click here")}
                    </span>
                  </div>
                )}
                {phase === "saved" && (
                  <div className="osint-actrow">
                    <button type="button" className="osint-run wide go" onClick={() => setPhase("scan")}>
                      {"\u{1F578}"} {txr("Deep-scan social profiles: {name}", { name: config.target.name })}
                    </button>
                    <span className="osint-clickhere osint-clickhere--right" aria-hidden onClick={() => setPhase("scan")}>
                      <span className="osint-clickhere-arrow">{"\u2190"}</span> {tx("Click here")}
                    </span>
                  </div>
                )}
                {(phase === "scan" || phase === "profile") && (
                  <div className="osint-saved-note">{"\u2713"} {txr("target_list.csv saved · scanning {name}", { name: config.target.name })}</div>
                )}
              </>
            )}
          </div>

          {/* Right: deep social scan -> victim profile */}
          <div className="osint-right">
            <div className="osint-card-h">{tx("Social intelligence")}</div>
            {phase !== "scan" && phase !== "profile" ? (
              <div className="osint-empty">{tx("Save a target, then scan their public footprint.")}</div>
            ) : (
              <div className="osint-profile">
                <div className="osint-profile-head">
                  <span className="osint-profile-pic">
                    {config.target.photoUrl ? <img src={config.target.photoUrl} alt="" /> : config.target.name.slice(0, 1)}
                  </span>
                  <span className="osint-profile-text">
                    <span className="osint-profile-name">{config.target.name}</span>
                    <span className="osint-profile-mail">{config.target.email}</span>
                  </span>
                </div>
                <ul className="osint-findings">
                  {findings.map((f, i) => {
                    const shown = phase === "profile" || i <= scanIdx;
                    const meta = SOURCE_META[f.source] ?? { ico: "\u{1F310}", label: f.source };
                    return (
                      <li key={i} className={`osint-finding ${shown ? "in" : ""}`}>
                        <span className="osint-finding-ico" aria-hidden>{meta.ico}</span>
                        <span className="osint-finding-text">
                          <span className="osint-finding-src">{meta.label}</span>
                          <span className="osint-finding-label">{f.label}</span>
                          <span className="osint-finding-detail">{f.detail}</span>
                        </span>
                        {f.photoUrl && shown && (
                          <span className="osint-finding-pic">
                            <img src={f.photoUrl} alt="" />
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {phase === "profile" && (
                  <button
                    type="button"
                    className={`btn osint-continue ${speaking ? "speaking" : ""}`}
                    onClick={onContinue}
                    disabled={speaking}
                  >
                    {config.continueLabel ?? tx("Build the spear-phish")}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
